import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import logger from '@/lib/logger';
import { supabase } from '@/lib/supabaseClient';
import { UserRole, Register } from '@webready/shared/schema';
import { useToast } from '@/hooks/use-toast';
import { fetchWithSchema, getSchemaHeader } from '@/lib/api';

export interface UserData {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  phone?: string;
  photoUrl?: string;
  specialty?: string;
  bio?: string;
  employeeId?: number; // ID-ul din tabelul employees
}

export interface AuthContextType {
  user: any | null; // Changed from User | null to any | null
  userData: UserData | null;
  loading: boolean;
  register: (data: Register) => Promise<void>;
  login: (email: string, password: string) => Promise<UserData | null>;
  logout: () => Promise<void>;
  updateProfile: (data: Partial<UserData>) => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<any>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const checkUserSession = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase.auth.getUser();
        
        if (error) {
          setUser(null);
          setUserData(null);
        } else if (data?.user) {
          /* console.log('🔐 [AuthContext] Setting user from session:', {
            userId: data.user.id,
            email: data.user.email,
            hasAccessToken: !!data.user.access_token,
            accessTokenLength: data.user.access_token?.length || 0,
            userObject: data.user
          }); */
          setUser(data.user);
          // Fetch user profile from users table using API with schema
          let profileResponse = await fetchWithSchema(`/api/users/${data.user.id}`);
          
          // If we get a 401, try to refresh the session and retry once
          if (profileResponse.status === 401) {
            try {
              logger.debug('Auth: Profile fetch returned 401, attempting to refresh session...');
              const { data: { session }, error: refreshError } = await supabase.auth.refreshSession();
              
              if (!refreshError && session?.access_token) {
                // Retry the profile fetch with the refreshed token
                logger.debug('Auth: Session refreshed, retrying profile fetch...');
                profileResponse = await fetchWithSchema(`/api/users/${data.user.id}`);
              } else {
                logger.error('Auth: Failed to refresh session after 401', refreshError);
                // If refresh fails, clear everything
                setUser(null);
                setUserData(null);
              }
            } catch (refreshErr) {
              logger.error('Auth: Error refreshing session', refreshErr);
              // If refresh fails, clear everything
              setUser(null);
              setUserData(null);
            }
          }
          
          if (!profileResponse.ok) {
            const profileError = await profileResponse.json().catch(() => ({ message: 'Unknown error' }));
            logger.error('Auth: profile fetch error after retry', { 
              status: profileResponse.status, 
              error: profileError 
            });
            // For 401 errors, we already tried to refresh above
            // If it still fails after refresh, the session is truly invalid - clear everything
            // For other errors (500, network, etc.), keep user but log error
            // Don't clear userData immediately - let components handle gracefully
            // This prevents premature redirects on transient errors
            if (profileResponse.status === 401) {
              // Session is invalid after refresh attempt - clear everything
              setUser(null);
              setUserData(null);
            }
            // For other errors, don't clear userData - keep existing state to prevent redirect loops
          } else {
            const profile = await profileResponse.json();
            let employeeId: number | undefined;

            // If user is employee, fetch employee record to get employeeId
            if (profile.role === 'employee') {
              try {
                // First try the debug endpoint (temporary solution)
                const debugResponse = await fetchWithSchema(`/api/employees/debug/by-user/${data.user.id}`, {
                  method: 'GET',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  credentials: 'include'
                });

                if (debugResponse.ok) {
                  const debugData = await debugResponse.json();

                  if (debugData.success && debugData.employee) {
                    employeeId = debugData.employee.id;
                  }
                } else {
                  // Try the regular API endpoint
                  const response = await fetchWithSchema(`/api/employees/by-user/${data.user.id}`, {
                    method: 'GET',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
                    },
                    credentials: 'include'
                  });

                  if (response.ok) {
                    const employeeData = await response.json();

                    if (employeeData && employeeData.id) {
                      employeeId = employeeData.id;
                    }
                  } else {
                    const errorData = await response.json().catch(() => ({}));
                    logger.error('Auth: regular API failed', { status: response.status, error: errorData });
                  }
                }
              } catch (error) {
                logger.error('Auth: employee fetch error', error);
              }
            }

            const userDataObj = {
              id: data.user.id, // Use the actual UUID from Supabase Auth
              firstName: typeof profile.first_name === 'string' ? profile.first_name : '',
              lastName: typeof profile.last_name === 'string' ? profile.last_name : '',
              email: typeof profile.email === 'string' ? profile.email : '',
              role: typeof profile.role === 'string' ? profile.role : '',
              phone: typeof profile.phone === 'string' ? profile.phone : undefined,
              photoUrl: typeof profile.photo_url === 'string' ? profile.photo_url : undefined,
              specialty: typeof profile.specialty === 'string' ? profile.specialty : undefined,
              bio: typeof profile.bio === 'string' ? profile.bio : undefined,
              employeeId: employeeId,
            };

            setUserData(userDataObj);
          }
        } else {
          setUser(null);
          setUserData(null);
        }
      } catch (error) {
        console.error('💥 [AuthContext] Session check error:', error);
        setUser(null);
        setUserData(null);
        logger.error('Auth: session check error');
      } finally {
        setLoading(false);
      }
    };
    checkUserSession();
  }, []);

  const register = async (data: Register) => {
    const {
      email,
      password,
      first_name,
      last_name,
      phone,
      role,
      address,
      bio,
      photo_url,
      specialty,
      secretCode
    } = data as any;

    logger.info('[REGISTER] Starting registration process', { 
      email, 
      role, 
      hasPassword: !!password,
      hasFirstName: !!first_name,
      hasLastName: !!last_name,
      hasSecretCode: !!secretCode
    });

    try {
      // Use backend API endpoint for registration
      // This ensures user is created in the correct client schema
      logger.debug('[REGISTER] Calling /api/auth/register endpoint');
      
      const registerPayload: any = {
        email,
        password,
        first_name,
        last_name,
        phone: phone ?? null,
        role,
      };

      // Add optional fields
      if (address) registerPayload.address = address;
      if (bio) registerPayload.bio = bio;
      if (photo_url) registerPayload.photo_url = photo_url;
      if (specialty) registerPayload.specialty = specialty;
      if (secretCode) {
        registerPayload.secretCode = secretCode;
        registerPayload.secret_code = secretCode;
      }

      logger.debug('[REGISTER] Register payload', { 
        ...registerPayload,
        password: '[REDACTED]'
      });

      const response = await fetchWithSchema('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registerPayload),
      });

      logger.debug('[REGISTER] Register response status', { 
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        logger.error('[REGISTER] Register failed', { 
          status: response.status,
          error: errorData.message || errorData.error || 'Registration failed',
          details: errorData
        });
        throw new Error(errorData.message || errorData.error || 'Registration failed');
      }

      const result = await response.json();
      logger.info('[REGISTER] Registration successful', { 
        userId: result.user?.id,
        email: result.user?.email,
        role: result.user?.role
      });

      toast({ title: 'Account created successfully', description: 'Welcome to AppName!' });
    } catch (error: any) {
      logger.error('[REGISTER] Registration failed', { 
        error: error.message,
        details: error,
        stack: error.stack
      });
      throw error;
    }
  };

  const login = async (email: string, password: string): Promise<UserData | null> => {
    try {
      // 1. Log: Supabase conectat și token
      logger.debug('[AUTH] start login');
      const sessionRes = await supabase.auth.getSession();
      const userRes = await supabase.auth.getUser();
      logger.debug('[AUTH] getSession/getUser done');
      const token = sessionRes.data?.session?.access_token;
      if (!token) {
        logger.warn('[AUTH] no JWT token');
      } else {
        const shortToken = token.slice(0, 5) + '...' + token.slice(-5);
        logger.debug('[AUTH] token', shortToken);
      }
      const userRole = userRes.data?.user?.role || userRes.data?.user?.user_metadata?.role;
      logger.debug('[AUTH] role', userRole);

      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      
      /* console.log('🔐 [AuthContext] Login successful, verifying user in client schema:', {
        userId: data.user.id,
        email: data.user.email
      }); */
      
      // IMPORTANT: Do NOT set user yet - first verify they exist in client's schema
      // Check if user exists in client's schema using API BEFORE setting user state
      logger.debug('[AUTH] checking user in client schema via API BEFORE setting user');
      
      try {
        // Debug: Log schema before request
        const schemaHeader = getSchemaHeader();
        // console.log('🔍 [AuthContext] Schema header:', schemaHeader);
        
        const profileResponse = await fetchWithSchema('/api/auth/me');
        
        /* console.log('🔍 [AuthContext] Profile response status:', profileResponse.status);
        console.log('🔍 [AuthContext] Profile response headers:', Object.fromEntries(profileResponse.headers.entries())); */
        
        if (!profileResponse.ok) {
          // Try to get error message from response
          let errorText = '';
          try {
            const contentType = profileResponse.headers.get('content-type');
            if (contentType?.includes('application/json')) {
              const errorData = await profileResponse.json();
              errorText = JSON.stringify(errorData);
            } else {
              errorText = await profileResponse.text();
            }
          } catch (e) {
            errorText = 'Could not parse error response';
          }
          
          logger.error('[AUTH] User not found in client schema - REJECTING LOGIN', {
            status: profileResponse.status,
            statusText: profileResponse.statusText,
            errorText,
            userId: data.user.id,
            email: data.user.email
          });
          
          // User exists in auth but not in client's schema - reject login immediately
          await supabase.auth.signOut();
          toast({
            title: 'Acces refuzat',
            description: `Userul nu există în baza de date a acestui client. (${profileResponse.status} ${profileResponse.statusText})`,
            variant: 'destructive'
          });
          return null;
        }
        
        // Parse JSON response
        let profile;
        try {
          const contentType = profileResponse.headers.get('content-type');
          if (!contentType?.includes('application/json')) {
            const text = await profileResponse.text();
            console.error('❌ [AuthContext] Response is not JSON:', text.substring(0, 200));
            throw new Error(`Expected JSON but got ${contentType}. Response: ${text.substring(0, 200)}`);
          }
          profile = await profileResponse.json();
        } catch (parseError) {
          logger.error('[AUTH] Error parsing profile response', {
            error: parseError,
            status: profileResponse.status,
            statusText: profileResponse.statusText
          });
          throw new Error(`Failed to parse profile response: ${parseError instanceof Error ? parseError.message : 'Unknown error'}`);
        }
        
        logger.debug('[AUTH] user profile loaded from API', { userId: profile.id, role: profile.role });
        
        if (!profile) {
          logger.error('[AUTH] profile is null - REJECTING LOGIN');
          await supabase.auth.signOut();
          toast({
            title: 'Acces refuzat',
            description: 'Profilul utilizatorului nu a fost găsit.',
            variant: 'destructive'
          });
          return null;
        }
        
        // Only NOW set the user state - user exists in client's schema
        /* console.log('🔐 [AuthContext] User verified in client schema, setting user state:', {
          userId: data.user.id,
          email: data.user.email
        }); */
        
        setUser(data.user);
        
        const userDataObj = {
          id: data.user.id, // Use the actual UUID from Supabase Auth
          firstName: typeof profile.first_name === 'string' ? profile.first_name : '',
          lastName: typeof profile.last_name === 'string' ? profile.last_name : '',
          email: typeof profile.email === 'string' ? profile.email : '',
          role: typeof profile.role === 'string' ? profile.role : '',
          phone: typeof profile.phone === 'string' ? profile.phone : undefined,
          photoUrl: typeof profile.photo_url === 'string' ? profile.photo_url : undefined,
          specialty: typeof profile.specialty === 'string' ? profile.specialty : undefined,
          bio: typeof profile.bio === 'string' ? profile.bio : undefined,
        };
        setUserData(userDataObj);
        return userDataObj;
      } catch (profileError) {
        logger.error('[AUTH] Error checking user in client schema - REJECTING LOGIN', profileError);
        await supabase.auth.signOut();
        toast({
          title: 'Eroare de autentificare',
          description: 'Nu s-a putut verifica utilizatorul în baza de date a clientului.',
          variant: 'destructive'
        });
        return null;
      }
    } catch (error) {
      setUser(null);
      setUserData(null);
      logger.error('[AUTH] login failed');
      throw error;
    }
  };

  const logout = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      setUserData(null);
    } catch (error) {
      logger.error('Logout error');
    }
  };

  const updateProfile = async (data: Partial<UserData>) => {
    if (!user || !userData) return;
    const payload = {
      first_name: data.firstName,
      last_name: data.lastName,
      email: data.email,
      role: data.role,
      phone: data.phone,
      photo_url: data.photoUrl,
      specialty: data.specialty,
      bio: data.bio,
    };
    try {
      const response = await fetchWithSchema(`/api/users/${userData.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) logger.error('Failed to update user profile');
    } catch (err) {
      logger.error('Failed to update user profile', err);
    }
    setUserData((prev) => prev ? { ...prev, ...data } : null);
  };

  const value = { user, userData, loading, register, login, logout, updateProfile };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};